import { useEffect, useState } from "react";
import { useCustom, useCustomMutation, usePermissions, useTranslate } from "@refinedev/core";
import { ActionBar, Button, FormCard, SectionHeader } from "@fawkes/ui";

import type { Prijsberekening } from "../pensionplanning/accommodatie";
import "../pensionplanning/AccommodatieBeheer.css";

// settings-pension: "Prijsberekening" — hoe de vestiging een verblijf berekent. De prijzen zelf staan bij
// Producten › Pensiontarieven (de pensionprijzen, en de prijzen van de accommodatietypes); kortingen, zoals op het
// tweede dier, staan daar op het tabblad Kortingen. Wijzigen vraagt ROLE_PENSION_ADMIN, net als de api.

interface Instelling {
  vestigingId: number;
  vestigingCode: string | null;
  prijsberekening: Prijsberekening;
  uitcheckGratisTot: string | null;
  uitcheckHalveDagTot: string | null;
  incheckdagVolledig: boolean;
}

export function PensionPrijsberekening({ vestigingId, onBack }: { vestigingId: number; onBack: () => void }) {
  const t = useTranslate();
  const url = `pension/prijsberekening?vestigingId=${vestigingId}`;
  const { data, isLoading, isError, refetch } = useCustom<Instelling>({ url, method: "get" });
  const { mutate, isLoading: bezig } = useCustomMutation();
  const { data: rechten } = usePermissions<string[]>();
  const magBeheren = (rechten ?? []).includes("ROLE_PENSION_ADMIN");

  const [berekening, setBerekening] = useState<Prijsberekening>("PENSIONPRIJZEN");
  const [gratisTot, setGratisTot] = useState("");
  const [halveDagTot, setHalveDagTot] = useState("");
  const [incheckdag, setIncheckdag] = useState(true);
  const [opgeslagen, setOpgeslagen] = useState(false);
  const [fout, setFout] = useState(false);

  const huidig = data?.data;
  useEffect(() => {
    if (!huidig) return;
    setBerekening(huidig.prijsberekening);
    setGratisTot(huidig.uitcheckGratisTot?.slice(0, 5) ?? "");
    setHalveDagTot(huidig.uitcheckHalveDagTot?.slice(0, 5) ?? "");
    setIncheckdag(huidig.incheckdagVolledig);
  }, [huidig]);

  const geldig = !(gratisTot && halveDagTot && halveDagTot <= gratisTot);
  const wijzig = () => setOpgeslagen(false);

  const bewaar = () => {
    if (!geldig) return;
    setFout(false);
    setOpgeslagen(false);
    mutate(
      {
        url,
        method: "put",
        values: {
          prijsberekening: berekening,
          uitcheckGratisTot: gratisTot || null,
          uitcheckHalveDagTot: halveDagTot || null,
          incheckdagVolledig: incheckdag,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setOpgeslagen(true);
          void refetch();
        },
        onError: () => setFout(true),
      },
    );
  };

  return (
    <FormCard>
      <SectionHeader>{t("pensionPrijsberekening.titel")}</SectionHeader>
      <div className="acc-beheer">
        {isLoading ? (
          <p className="fawkes-muted">{t("common.loading")}</p>
        ) : isError ? (
          <p className="fawkes-error">{t("pensionPrijsberekening.laden")}</p>
        ) : (
          <form
            className="acc-kaart"
            aria-label={t("pensionPrijsberekening.titel")}
            onSubmit={(e) => {
              e.preventDefault();
              bewaar();
            }}
          >
            <p className="acc-leeg">{t("pensionPrijsberekening.uitleg")}</p>
            <fieldset className="acc-keuzes" disabled={!magBeheren}>
              <legend className="acc-verborgen">{t("pensionPrijsberekening.titel")}</legend>
              {(["PENSIONPRIJZEN", "ACCOMMODATIE"] as const).map((keuze) => (
                <label key={keuze} className="acc-keuze" data-gekozen={berekening === keuze ? "" : undefined}>
                  <input
                    type="radio"
                    name="prijsberekening"
                    checked={berekening === keuze}
                    onChange={() => {
                      setBerekening(keuze);
                      wijzig();
                    }}
                  />
                  <span>
                    <strong>{t(`pensionPrijsberekening.${keuze}`)}</strong>
                    <span>{t(`pensionPrijsberekening.${keuze}.uitleg`)}</span>
                  </span>
                </label>
              ))}
            </fieldset>
            <div className="acc-velden">
              <label>
                <span>{t("pensionPrijsberekening.gratisTot")}</span>
                <input type="time" value={gratisTot} disabled={!magBeheren}
                  onChange={(e) => { setGratisTot(e.target.value); wijzig(); }} />
              </label>
              <label>
                <span>{t("pensionPrijsberekening.halveDagTot")}</span>
                <input type="time" value={halveDagTot} disabled={!magBeheren}
                  onChange={(e) => { setHalveDagTot(e.target.value); wijzig(); }} />
              </label>
              <label className="acc-vink">
                <input type="checkbox" checked={incheckdag} disabled={!magBeheren}
                  onChange={(e) => { setIncheckdag(e.target.checked); wijzig(); }} />
                <span>{t("pensionPrijsberekening.incheckdag")}</span>
              </label>
            </div>
            {!geldig && <p className="acc-fout">{t("pensionPrijsberekening.halveDagOngeldig")}</p>}
            <p className="acc-leeg">{t("pensionPrijsberekening.prijzen")}</p>
            <p className="acc-leeg">{t("pensionPrijsberekening.later")}</p>
            {!magBeheren && <p className="acc-leeg">{t("pensionPrijsberekening.alleenLezen")}</p>}
            {fout && (
              <p className="acc-fout" role="alert">
                {t("pensionPrijsberekening.fout")}
              </p>
            )}
            {opgeslagen && <p className="acc-opgeslagen">{t("pensionPrijsberekening.opgeslagen")}</p>}
            {magBeheren && (
              <div className="acc-voet">
                <button type="submit" className="acc-knop acc-knop--hoofd" disabled={bezig || !geldig}>
                  {t("common.save")}
                </button>
              </div>
            )}
          </form>
        )}
      </div>
      <ActionBar>
        <Button variant="secondary" onClick={onBack} disabled={bezig}>
          {t("common.back")}
        </Button>
      </ActionBar>
    </FormCard>
  );
}
